import { z } from "zod";
/**
 * THE BUSINESS'S SOCIAL NETWORKS («Redes sociales» of the public profile).
 * The owner types a
 * handle or pastes whatever link the app gave them; the column keeps the
 * handle alone and the address is built from it, so a wrong rule here sends a
 * visitor somewhere else without anyone noticing. They are the business's own
 * word, never a verified signal.
 */
/** In the order the profile and the settings list them. */
export const SOCIAL_NETWORKS = ["instagram", "facebook", "tiktok"];
export const SOCIAL_NETWORK_NAMES = { instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok" };
/** What every field says while empty. */
export const SOCIAL_PLACEHOLDER = "@tu_negocio o pega el enlace";
const VIDEO_MESSAGE = "Pega el enlace de tu perfil, no el de un video.";
const POST_MESSAGE = "Pega el enlace de tu perfil, no el de una publicación.";
const NO_USER_MESSAGE = "A ese enlace le falta tu usuario.";
const NO_PAGE_NUMBER_MESSAGE = "A ese enlace le falta el número de tu página.";
const FACEBOOK_SHARE_MESSAGE = "Ese enlace corto no dice cuál es tu página. Abre tu página en Facebook y copia su dirección.";
/**
 * A Facebook page with no username lives at `profile.php?id=<digits>` or
 * `/p/<its name>-<digits>/` -- many in Panama never chose one, so refusing
 * them would leave those businesses out. The column keeps it as `id:<digits>`, which no username can be (a colon
 * is not one of its characters).
 */
const FACEBOOK_ID_PREFIX = "id:";
const FACEBOOK_ID = /^id:[0-9]{5,20}$/;
const DIGITS = /^[0-9]{5,20}$/;
const PAGE_NAME_NUMBER = /-([0-9]{5,20})$/;
/** Instagram's first segments that are never a user: posts, reels, stories and its own pages. */
const INSTAGRAM_PAGES = new Set(["p", "reel", "reels", "tv", "stories", "explore", "accounts", "direct", "about", "legal"]);
/** Facebook's first segments that are never a page, and what to say about each. */
const FACEBOOK_VIDEO_PAGES = new Set(["watch", "reel", "reels", "videos"]);
const FACEBOOK_PAGES = new Set(["groups", "events", "marketplace", "stories", "photo", "photos", "posts", "hashtag", "search", "pages", "gaming", "login", "home"]);
const raw = (handle) => ({ ok: true, handle });
const refused = (message) => ({ ok: false, message });
function queryValue(query, name) {
    for (const pair of query.split("&")) {
        const [key, value] = pair.split("=");
        if (key === name)
            return value ?? "";
    }
    return null;
}
function facebookFromPath({ segments, query }) {
    const [first, ...rest] = segments;
    if (first === undefined)
        return refused(NO_USER_MESSAGE);
    const page = first.toLowerCase();
    if (page === "profile.php") {
        const id = queryValue(query, "id");
        return id !== null && DIGITS.test(id) ? raw(`${FACEBOOK_ID_PREFIX}${id}`) : refused(NO_PAGE_NUMBER_MESSAGE);
    }
    // The address Facebook gives a page with no username: /people/<its name>/<its number>/.
    if (page === "people") {
        const id = rest[1];
        return rest.length === 2 && id !== undefined && DIGITS.test(id) ? raw(`${FACEBOOK_ID_PREFIX}${id}`) : refused(NO_PAGE_NUMBER_MESSAGE);
    }
    // Its newer form, and today the common one: /p/<its name>-<its number>/.
    if (page === "p") {
        const id = PAGE_NAME_NUMBER.exec(rest[0] ?? "")?.[1];
        return rest.length === 1 && id !== undefined ? raw(`${FACEBOOK_ID_PREFIX}${id}`) : refused(NO_PAGE_NUMBER_MESSAGE);
    }
    if (page === "share")
        return refused(FACEBOOK_SHARE_MESSAGE);
    if (FACEBOOK_VIDEO_PAGES.has(page) || rest[0]?.toLowerCase() === "videos")
        return refused(VIDEO_MESSAGE);
    if (FACEBOOK_PAGES.has(page) || page.endsWith(".php") || rest.length > 0)
        return refused(POST_MESSAGE);
    return raw(first);
}
const RULES = {
    instagram: {
        pattern: /^[a-z0-9._]{1,30}$/,
        caseless: true,
        shapeMessage: "Un usuario de Instagram lleva de 1 a 30 letras, números, puntos o guiones bajos.",
        hosts: ["instagram.com"],
        short: null,
        fromPath: ({ segments }) => {
            const [first, ...rest] = segments;
            if (first === undefined)
                return refused(NO_USER_MESSAGE);
            return INSTAGRAM_PAGES.has(first.toLowerCase()) || rest.length > 0 ? refused(POST_MESSAGE) : raw(first);
        },
        url: (handle) => `https://www.instagram.com/${handle}/`,
    },
    facebook: {
        pattern: /^[A-Za-z0-9.]{5,50}$/,
        caseless: false,
        shapeMessage: "Un usuario de Facebook lleva de 5 a 50 letras, números o puntos.",
        hosts: ["facebook.com", "fb.com"],
        // Its short links (/share/…) live on the main host: `facebookFromPath` says so.
        short: null,
        fromPath: facebookFromPath,
        url: (handle) => handle.startsWith(FACEBOOK_ID_PREFIX)
            ? `https://www.facebook.com/profile.php?id=${handle.slice(FACEBOOK_ID_PREFIX.length)}`
            : `https://www.facebook.com/${handle}`,
    },
    tiktok: {
        pattern: /^[a-z0-9._]{2,24}$/,
        caseless: true,
        shapeMessage: "Un usuario de TikTok lleva de 2 a 24 letras, números, puntos o guiones bajos.",
        hosts: ["tiktok.com"],
        // vm./vt. links are resolved by TikTok's servers, and nothing offline says whose profile, if any, they open.
        short: { hosts: ["vm.tiktok.com", "vt.tiktok.com"], message: VIDEO_MESSAGE },
        fromPath: ({ segments }) => {
            const [first, ...rest] = segments;
            if (first === undefined)
                return refused(NO_USER_MESSAGE);
            // A profile is /@<user>; anything else, or deeper, is a video or one of TikTok's own pages.
            return first.startsWith("@") && rest.length === 0 ? raw(first.slice(1)) : refused(VIDEO_MESSAGE);
        },
        url: (handle) => `https://www.tiktok.com/@${handle}`,
    },
};
/** Scheme, host, path, query and fragment, read by hand: no URL parser, so the same on Hermes and on Node. */
const LINK = /^(?:https?:\/\/)?([^/?#\s]+)([^?#\s]*)(?:\?([^#\s]*))?(?:#\S*)?$/i;
const SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
/** The prefixes a network's own site answers on. */
const SUBDOMAIN = /^(?:www|m|web|mobile)\./;
function safeDecode(value) {
    try {
        return decodeURIComponent(value);
    }
    catch {
        return value;
    }
}
/** Every network's site, bare: a host typed alone is a link with no user, not a handle. */
const ALL_HOSTS = new Set(SOCIAL_NETWORKS.flatMap((network) => RULES[network].hosts));
function looksLikeLink(input) {
    return SCHEME.test(input) || input.includes("/") || ALL_HOSTS.has(input.toLowerCase().replace(SUBDOMAIN, ""));
}
function fromLink(network, input) {
    const rule = RULES[network];
    const notThis = `Ese enlace no es de ${SOCIAL_NETWORK_NAMES[network]}.`;
    const match = LINK.exec(input);
    if (match === null)
        return refused(notThis);
    const host = (match[1] ?? "").toLowerCase();
    if (rule.short?.hosts.includes(host))
        return refused(rule.short.message);
    // Exact, never `endsWith`: «instagram.com.evil.example» is not Instagram.
    if (!rule.hosts.includes(host.replace(SUBDOMAIN, "")))
        return refused(notThis);
    const segments = (match[2] ?? "").split("/").filter((segment) => segment !== "").map(safeDecode);
    return rule.fromPath({ segments, query: match[3] ?? "" });
}
/**
 * What the owner typed, as the handle the column keeps: «@user», «user», or
 * the network's link to the profile, query and all. Nothing written is no
 * network (null). A link elsewhere, to a post or a video, or a short link
 * only the network can resolve is refused with a sentence that says what to
 * paste instead. A stored handle reads as itself, so a value saved once saves
 * again unchanged.
 */
export function socialHandleOf(network, input) {
    const rule = RULES[network];
    const typed = input.trim();
    if (typed === "")
        return raw(null);
    const read = looksLikeLink(typed) ? fromLink(network, typed) : raw(typed.startsWith("@") ? typed.slice(1) : typed);
    if (!read.ok || read.handle === null)
        return read;
    if (network === "facebook" && FACEBOOK_ID.test(read.handle))
        return read;
    const handle = rule.caseless ? read.handle.toLowerCase() : read.handle;
    return rule.pattern.test(handle) ? raw(handle) : refused(rule.shapeMessage);
}
/** Where a visitor goes. Only for a handle `socialHandleOf` made. */
export function socialUrlOf(network, handle) {
    return RULES[network].url(handle);
}
/** How the profile names it: «@user»; a Facebook page with no username, by its network. */
export function socialLabelOf(network, handle) {
    return handle.startsWith(FACEBOOK_ID_PREFIX) && network === "facebook" ? SOCIAL_NETWORK_NAMES.facebook : `@${handle}`;
}
/** «Instagram de <negocio>, @handle»; a numbered Facebook page shows no handle, so its network alone. */
export function socialLinkName(link, business) {
    const name = `${SOCIAL_NETWORK_NAMES[link.network]} de ${business}`;
    const label = socialLabelOf(link.network, link.handle);
    return label.startsWith("@") ? `${name}, ${label}` : name;
}
/** What the settings' field shows for a saved handle: what the profile shows, or the link of a numbered page -- either reads back as the same handle. */
export function socialFieldOf(network, handle) {
    return handle.startsWith(FACEBOOK_ID_PREFIX) && network === "facebook" ? socialUrlOf(network, handle).replace("https://www.", "") : `@${handle}`;
}
/** Every field of «Redes sociales» for the saved handles: `socialFieldOf`, or empty for a network not set. */
export function socialFieldsOf(handles) {
    const field = (network) => {
        const handle = handles[network];
        return handle === null ? "" : socialFieldOf(network, handle);
    };
    return { instagram: field("instagram"), facebook: field("facebook"), tiktok: field("tiktok") };
}
/** The networks the business set, in `SOCIAL_NETWORKS` order, each with its address. */
export function socialLinksOf(handles) {
    const links = [];
    for (const network of SOCIAL_NETWORKS) {
        const handle = handles[network];
        if (handle !== null)
            links.push({ network, handle, url: socialUrlOf(network, handle) });
    }
    return links;
}
const CHECK_DATA_MESSAGE = "Revisa los datos enviados.";
const handleField = (network) => z
    .string({ error: CHECK_DATA_MESSAGE })
    .nullable()
    .transform((input, ctx) => {
    if (input === null)
        return null;
    const read = socialHandleOf(network, input);
    if (read.ok)
        return read.handle;
    ctx.issues.push({ code: "custom", message: read.message, input });
    return z.NEVER;
});
/** What «Guardar» sends for the networks: each one's field as typed, or null; the server keeps the handles. */
export const socialHandlesSchema = z.strictObject({ instagram: handleField("instagram"), facebook: handleField("facebook"), tiktok: handleField("tiktok") }, { error: CHECK_DATA_MESSAGE });
